/**
 * Paper-and-clay friendly error screen.
 *
 * Shown by GlobalErrorBoundary when a React render throws, and also
 * mountable as a standalone `/error` route for last-resort transitions.
 *
 * The reference code (`PCE-XXXXXXXX`) is the human-shareable id minted
 * by errors.rpc_ingest_error. Users are told to email support with it;
 * support pastes it into the admin Errors list to land on the matching
 * group instantly.
 */

import { useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Card } from './Card';
import { Display } from './Display';
import { SmallCap } from './SmallCap';

export interface ErrorFallbackProps {
  /** The PCE-XXXXXXXX id assigned by the ingest pipeline. */
  shortCode: string;
  /** Optional short headline; defaults to "Something went wrong on our end." */
  title?: ReactNode;
  /** Optional supporting paragraph above the action buttons. */
  description?: ReactNode;
  /** When true, show a "Reload" button. Default true. */
  showReload?: boolean;
  /** When true, show a "Back to home" button. Default true. */
  showHome?: boolean;
  /** Override the home href. Default "/". */
  homeHref?: string;
  /** Override the support email. Default "support@peptideclients.com". */
  supportEmail?: string;
  /** Optional sub-line above the buttons (e.g. "Reported in the background."). */
  hint?: ReactNode;
  /** Render-debug only: the original error message, behind a "Show details" toggle. */
  technicalDetails?: string | null;
}

const DEFAULT_TITLE = (
  <>
    Something <em>went wrong</em>.
  </>
);
const DEFAULT_DESCRIPTION =
  'A copy of the error has been sent to our team automatically. If this keeps happening, please email support with the reference code below and we will dig in.';
const DEFAULT_SUPPORT = 'support@peptideclients.com';

export function ErrorFallback({
  shortCode,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  showReload = true,
  showHome = true,
  homeHref = '/',
  supportEmail = DEFAULT_SUPPORT,
  hint,
  technicalDetails = null,
}: ErrorFallbackProps) {
  const [copied, setCopied] = useState(false);
  const [showTech, setShowTech] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(shortCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers: select+execCommand fallback. Best-effort; not
      // worth a polyfill blob for the long tail.
      setCopied(false);
    }
  }

  const mailto =
    `mailto:${supportEmail}` +
    `?subject=${encodeURIComponent(`PeptideClients error ${shortCode}`)}` +
    `&body=${encodeURIComponent(
      [
        `Hi PeptideClients team,`,
        ``,
        `I hit an error in your app.`,
        ``,
        `Reference: ${shortCode}`,
        `Page:      ${typeof window !== 'undefined' ? window.location.href : ''}`,
        `When:      ${new Date().toISOString()}`,
        ``,
        `Here's what I was doing when it happened:`,
        ``,
      ].join('\n'),
    )}`;

  return (
    <div className="min-h-screen grid place-items-center bg-paper px-6 py-12">
      <div className="w-full max-w-xl flex flex-col gap-6">
        <SmallCap>Unexpected error</SmallCap>
        <Display size="lg">{title}</Display>
        <p className="text-base text-ink-2 max-w-prose leading-relaxed">{description}</p>

        <Card>
          <div className="flex flex-col gap-4">
            <div>
              <SmallCap>Reference code</SmallCap>
              <div className="mt-2 flex items-center gap-3">
                <code className="text-display-md font-medium tabular tracking-wide text-ink">
                  {shortCode}
                </code>
                <Button size="sm" variant="ghost" onClick={onCopy} type="button">
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="mt-2 text-sm text-muted">
                Include this code when you contact support.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <SmallCap>Email support</SmallCap>
              <a
                href={mailto}
                className="text-sm text-ink underline hover:text-clay break-all"
              >
                {supportEmail}
              </a>
            </div>

            {hint ? <p className="text-sm text-muted">{hint}</p> : null}
          </div>
        </Card>

        <div className="flex flex-wrap gap-3">
          {showReload ? (
            <Button
              variant="primary"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
              type="button"
            >
              Reload this page
            </Button>
          ) : null}
          {showHome ? (
            <Button
              variant="ghost"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.assign(homeHref);
              }}
              type="button"
            >
              Back to home
            </Button>
          ) : null}
        </div>

        {technicalDetails ? (
          <div className="text-xs text-muted">
            <button
              type="button"
              onClick={() => setShowTech((v) => !v)}
              className="underline hover:text-ink"
            >
              {showTech ? 'Hide technical details' : 'Show technical details'}
            </button>
            {showTech ? (
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words border border-rule bg-paper-2 p-3 text-[11px] leading-tight text-ink-2">
                {technicalDetails}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
